import { useEffect, useRef, useState } from "react";
import { uploadCategory } from "@/lib/uploadCategory";
import { atelierChoice, resetAtelier } from "@/lib/atelierChoice";
import { AtelierSent } from "@/components/atelier/AtelierSent";
import { Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { translateError } from "@/i18n/errors";

export const LinkRow = ({ item }) => {
  const { t, fmtBytes } = useI18n();
  if (item.status === "done" && item.atelier) {
    return (
      <li className="min-w-0 rounded-xl border border-[rgba(245,197,66,0.18)] bg-black/30 px-3 py-2 text-xs">
        <p dir="ltr" className="truncate text-neutral-500">{item.url}</p>
        <p className="mt-0.5" data-testid="link-import-result"><AtelierSent v={item.version} code={item.version_code} id={item.version_id} testId="link-import-atelier" /></p>
      </li>
    );
  }
  const msg = {
    queued: t("bulk.queued"),
    downloading: `${t("link.downloading")} ${item.bytes ? fmtBytes(item.bytes) : ""}`,
    duplicate: t("bulk.dup"),
    done: (item.result === "new_app" ? t("bulk.newApp", { name: item.app_name }) : t("bulk.newVersion", { name: item.app_name, v: item.label }))
      + (item.dedup ? ` · ${t("upload.dedup")}` : ""),
    error: item.message ? translateError({ response: { data: { detail: item.message } } }, t) : t("bulk.error"),
  }[item.status];
  const color = { done: "text-emerald-300", duplicate: "text-amber-200", error: "text-red-300" }[item.status] || "text-neutral-300";
  return (
    <li className="min-w-0 rounded-xl border border-[rgba(245,197,66,0.18)] bg-black/30 px-3 py-2 text-xs">
      <p dir="ltr" className="truncate text-neutral-500">{item.url}</p>
      <p className={`mt-0.5 flex items-center gap-1.5 ${color}`} data-testid="link-import-result">
        {["queued", "downloading"].includes(item.status) && <Loader2 className="h-3 w-3 animate-spin" />} {msg}
      </p>
    </li>
  );
};

// "Importer depuis un lien": one URL per line, downloaded server-side one at a time, status polled.
export const LinkImport = ({ onDone }) => {
  const { t } = useI18n();
  const { base } = useSpace();
  const [text, setText] = useState("");
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);
  const poll = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`${base}/link-imports/${id}`);
        setJob(data);
        if (data.done) return onDone?.();
      } catch {
        /* transient: keep polling */
      }
      poll(id);
    }, 1500);
  };
  const start = async () => {
    const urls = text.split(/\s*\n\s*/).map((u) => u.trim()).filter(Boolean);
    if (!urls.length) return;
    try {
      const { data } = await api.post(`${base}/link-imports`, { urls, category: uploadCategory(base), ...(base === "/admin" ? atelierChoice() : {}) });
      resetAtelier();
      setJob(data);
      setText("");
      poll(data.id);
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  const running = job && !job.done;
  return (
    <div className="mt-4 min-w-0 space-y-2 border-t border-[rgba(245,197,66,0.15)] pt-4" data-testid="link-import">
      <p className="flex items-center gap-2 text-sm font-semibold text-white"><Link2 className="h-4 w-4 text-[var(--gold)]" /> {t("link.title")}</p>
      <p className="text-xs text-neutral-500">{t("link.hint")}</p>
      <Textarea dir="ltr" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="https://drive.google.com/file/d/…/view" data-testid="link-import-input" />
      <button type="button" className="btn-gold btn-sm" disabled={running || !text.trim()} onClick={start} data-testid="link-import-start-btn">
        {running && <Loader2 className="h-4 w-4 animate-spin" />} {t("link.start")}
      </button>
      {job && <ul className="space-y-2" data-testid="link-import-list">{job.items.map((it, i) => <LinkRow key={i} item={it} />)}</ul>}
    </div>
  );
};
